import React, { useState, useEffect } from 'react';
import { View, Button, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import CustomModal from './ui/CustomModal.web';
import { ThemedText } from './ThemedText';

const IOS_HIGHLIGHT_BG = '#E6F0F7';
const IOS_HIGHLIGHT_FG = '#0a7ea4';

const DISMISS_KEY = 'pwa-install-dismissed';
const IOS_PROMPT_DELAY_MS = 1500;

const readDismissed = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage?.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

const writeDismissed = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(DISMISS_KEY, '1');
  } catch {
    // localStorage can throw in private mode on some browsers; ignore.
  }
};

type IOSMode = 'safari' | 'in-app-browser';

const detectIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true;
  // iPadOS 13+ reports as MacIntel; differentiate via touch points.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

const detectIOSMode = (): IOSMode => {
  if (typeof navigator === 'undefined') return 'safari';
  // Non-Safari iOS browsers (still WebKit, but can't trigger Add-to-Home-Screen).
  if (/CriOS|FxiOS|EdgiOS|FBAN|FBAV|Instagram|Line/.test(navigator.userAgent)) {
    return 'in-app-browser';
  }
  return 'safari';
};

const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  return (window.navigator as unknown as { standalone?: boolean }).standalone === true;
};

const PWAInstallPrompt = () => {
  const { t } = useTranslation();
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [iosMode, setIosMode] = useState<IOSMode | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || isStandalone()) return;
    // Ask once per browser. Chrome re-fires beforeinstallprompt on every
    // navigation until installed, and this component remounts across
    // route/redirect transitions — without this guard the modal would
    // re-open after every dismissal.
    if (readDismissed()) return;

    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsModalVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    let iosTimer: ReturnType<typeof setTimeout> | undefined;
    if (detectIOS()) {
      const mode = detectIOSMode();
      iosTimer = setTimeout(() => {
        setIosMode(mode);
        setIsModalVisible(true);
      }, IOS_PROMPT_DELAY_MS);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    installPrompt.userChoice.then((choice: { outcome?: string } | undefined) => {
      if (choice?.outcome === 'dismissed') {
        writeDismissed();
      }
      setInstallPrompt(null);
      setIsModalVisible(false);
    });
  };

  const handleDismiss = () => {
    writeDismissed();
    setIsModalVisible(false);
  };

  const renderBody = () => {
    if (iosMode === 'in-app-browser') {
      return (
        <ThemedText style={styles.modalText}>
          {t('install-app-ios-open-in-safari')}
        </ThemedText>
      );
    }
    if (iosMode === 'safari') {
      return (
        <View style={styles.stepsContainer}>
          <View style={styles.stepRow}>
            <ThemedText style={styles.stepNumber}>1.</ThemedText>
            <ThemedText style={styles.stepText}>
              {t('install-app-ios-step1')}
            </ThemedText>
          </View>
          <View style={styles.stepRow}>
            <ThemedText style={styles.stepNumber}>2.</ThemedText>
            <View style={styles.stepInlineRow}>
              <ThemedText style={styles.stepText}>
                {t('install-app-ios-step2-prefix')}{' '}
              </ThemedText>
              <View style={styles.shareHighlight}>
                <Ionicons
                  name="share-outline"
                  size={16}
                  color={IOS_HIGHLIGHT_FG}
                  style={styles.shareIcon}
                />
                <ThemedText style={styles.shareLabel}>
                  {t('install-app-ios-share-label')}
                </ThemedText>
              </View>
            </View>
          </View>
          <View style={styles.stepRow}>
            <ThemedText style={styles.stepNumber}>3.</ThemedText>
            <ThemedText style={styles.stepText}>
              {t('install-app-ios-step3-prefix')}{' '}
              <ThemedText style={styles.stepBold}>
                &ldquo;{t('install-app-ios-step3-target')}&rdquo;
              </ThemedText>
            </ThemedText>
          </View>
        </View>
      );
    }
    return (
      <ThemedText style={styles.modalText}>
        {t('install-app-description')}
      </ThemedText>
    );
  };

  return (
    <CustomModal
      visible={isModalVisible}
      onClose={handleDismiss}
      title={t('install-app-title')}
    >
      <View>
        {renderBody()}
        <View style={styles.buttonContainer}>
          {!iosMode && (
            <>
              <Button title={t('install-app')} onPress={handleInstall} />
              <View style={styles.buttonSpacer} />
            </>
          )}
          <Button title={t('dismiss')} onPress={handleDismiss} color="grey" />
        </View>
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  modalText: {
    marginBottom: 15,
    textAlign: 'center',
  },
  stepsContainer: {
    marginBottom: 15,
    width: '100%',
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginVertical: 4,
  },
  stepInlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexShrink: 1,
    flexWrap: 'wrap',
  },
  stepNumber: {
    width: 22,
    fontWeight: '600',
  },
  stepText: {
    flexShrink: 1,
  },
  stepBold: {
    fontWeight: '700',
  },
  shareHighlight: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: IOS_HIGHLIGHT_BG,
    borderColor: IOS_HIGHLIGHT_FG,
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  shareIcon: {
    marginRight: 4,
  },
  shareLabel: {
    fontWeight: '700',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
  },
  buttonSpacer: {
    width: 10,
  }
});

export default PWAInstallPrompt;
